import {
  createConnectQueryKey,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useTransport,
} from '@connectrpc/connect-query'
import { create } from '@bufbuild/protobuf'
import { useQueryClient } from '@tanstack/react-query'
import { useStore } from '@tanstack/react-store'
import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  cancelWorkflowExecution,
  cloneWorkflowDefinition as cloneWorkflowDefinitionRpc,
  createWorkflowDefinition,
  deleteWorkflowDefinition as deleteWorkflowDefinitionRpc,
  evaluateWorkflowConditions,
  getWorkflowExecution,
  getWorkflowDefinition,
  listWorkflowDefinitions,
  listWorkflowExecutions,
  retryWorkflowExecution,
  startWorkflowExecution,
  updateWorkflowDefinition,
  validateWorkflowDefinition as validateWorkflowDefinitionRpc,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  CancelWorkflowExecutionRequestSchema,
  CloneWorkflowDefinitionRequestSchema,
  DeleteWorkflowDefinitionRequestSchema,
  EvaluateWorkflowConditionsRequestSchema,
  GetWorkflowExecutionRequestSchema,
  GetWorkflowDefinitionRequestSchema,
  GetWorkflowDefinitionResponseSchema,
  ListWorkflowDefinitionsRequestSchema,
  ListWorkflowExecutionsRequestSchema,
  RetryWorkflowExecutionRequestSchema,
  StartWorkflowExecutionRequestSchema,
  UpdateWorkflowDefinitionRequestSchema,
  WorkflowExecutionStatus,
  WorkflowTriggerEntityType,
  type ValidateWorkflowDefinitionRequest,
  type WorkflowCondition,
  type WorkflowValidationIssue,
} from '@/gen/cortado/v1/cortado_pb'
import { smartRetry } from '@/lib/query-utils'
import { organizationStore } from '@/stores/organization.store'

export function useWorkflowDefinitions(options?: { enabled?: boolean }) {
  const selectedOrganizationId = useStore(
    organizationStore,
    (state) => state.selectedOrganizationId
  )

  const request = useMemo(
    () =>
      create(ListWorkflowDefinitionsRequestSchema, {
        organizationId: selectedOrganizationId || '',
      }),
    [selectedOrganizationId]
  )

  const query = useQuery(listWorkflowDefinitions, request, {
    enabled: !!selectedOrganizationId && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
  })

  return {
    workflowDefinitions: query.data?.workflowDefinitions || [],
    totalCount: query.data?.totalCount || 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

export function useGetWorkflowDefinition(id?: string) {
  const query = useQuery(
    getWorkflowDefinition,
    create(GetWorkflowDefinitionRequestSchema, {
      id: id ?? '',
    }),
    {
      enabled: !!id,
    }
  )

  return {
    workflowDefinition: query.data?.workflowDefinition,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

// Shared empty result so a not-yet-loaded validation doesn't hand callers a
// fresh array on every render (the editor memoizes issue routing on it).
const NO_VALIDATION_ISSUES: WorkflowValidationIssue[] = []

/**
 * useValidateWorkflowDefinition runs the backend's save-time validation against
 * a definition WITHOUT persisting it, returning field-keyed issues the builder
 * can route to the offending control. This is the editor's publish gate: the
 * client no longer mirrors the Go validators (see
 * internal-docs/plans/workflows-v2-node-runtime.md §4 R4).
 *
 * `request` IS the query key, so an unchanged definition is served from cache
 * and never re-validated — pass a memoized (and debounced) request. Callers
 * should skip the call entirely (`enabled: false`) for a definition that can't
 * produce a useful verdict; see canValidateOnServer in validate-definition.ts.
 *
 * The response is a pure function of the request and no server state, so window
 * focus never needs to re-run it.
 */
export function useValidateWorkflowDefinition(
  request: ValidateWorkflowDefinitionRequest,
  options?: { enabled?: boolean }
) {
  const query = useQuery(validateWorkflowDefinitionRpc, request, {
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: smartRetry,
  })

  return {
    valid: query.data?.valid,
    issues: query.data?.issues ?? NO_VALIDATION_ISSUES,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
    isError: query.isError,
    error: query.error,
  }
}

export function useWorkflowExecutions(params: {
  workflowDefinitionId?: string
  sourceResourceId?: string
  triggerEntityType?: string
  statuses?: WorkflowExecutionStatus[]
  pageSize?: number
  pageToken?: string
  enabled?: boolean
  includeDisplayContext?: boolean
  /**
   * Opt-in live refresh interval (ms) for lists that surface active runs. When
   * set, the query polls at this cadence ONLY while the loaded data contains at
   * least one active (RUNNING/PENDING) run; otherwise polling stays off so
   * terminal-only lists never poll. Default off (existing callers unchanged).
   */
  refetchActiveMs?: number
}) {
  const selectedOrganizationId = useStore(
    organizationStore,
    (state) => state.selectedOrganizationId
  )

  const {
    workflowDefinitionId,
    sourceResourceId,
    triggerEntityType,
    statuses = [],
    pageSize = 25,
    pageToken = '',
    enabled = true,
    includeDisplayContext = false,
    refetchActiveMs,
  } = params

  const request = useMemo(
    () =>
      create(ListWorkflowExecutionsRequestSchema, {
        organizationId: selectedOrganizationId || '',
        // These are proto-optional now: omit (undefined) when empty so the
        // field is genuinely absent on the wire and the backend skips the filter.
        workflowDefinitionId: workflowDefinitionId || undefined,
        sourceResourceId: sourceResourceId || undefined,
        triggerEntityType: triggerEntityType || undefined,
        statuses,
        pageSize,
        pageToken,
        includeDisplayContext,
      }),
    [
      selectedOrganizationId,
      workflowDefinitionId,
      sourceResourceId,
      triggerEntityType,
      statuses,
      pageSize,
      pageToken,
      includeDisplayContext,
    ]
  )

  const query = useQuery(listWorkflowExecutions, request, {
    // Enabled for the per-definition Runs tab (definition id), the per-entity
    // view (source resource id), or the global active-runs view (neither id,
    // relies on the caller's `enabled` / statuses). Always requires an org.
    enabled: !!selectedOrganizationId && enabled,
    staleTime: 30 * 1000,
    // Conservative opt-in live refresh: poll at refetchActiveMs only while the
    // loaded data still holds an active run; pause once everything is terminal.
    refetchInterval: refetchActiveMs
      ? (q) => {
          const executions = q.state.data?.executions ?? []
          const hasActive = executions.some(
            (execution) =>
              execution.status === WorkflowExecutionStatus.RUNNING ||
              execution.status === WorkflowExecutionStatus.PENDING
          )
          return hasActive ? refetchActiveMs : false
        }
      : false,
  })

  return {
    data: query.data,
    executions: query.data?.executions || [],
    nextPageToken: query.data?.nextPageToken || '',
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * useWorkflowExecutionsInfinite is the paginated (infinite) variant of
 * useWorkflowExecutions for surfaces that accumulate pages via "Load more".
 * Unlike a pageToken-keyed useQuery — which only ever refetches the LAST page,
 * freezing earlier ones — TanStack's infinite query refetches EVERY loaded page
 * on each refetch/poll tick, so a completed run on an earlier page flips off
 * "Running" without a manual reload. `refetchActiveMs` mirrors
 * useWorkflowExecutions: when set, poll at that cadence only while any loaded
 * run is still active (RUNNING/PENDING).
 */
export function useWorkflowExecutionsInfinite(params: {
  workflowDefinitionId?: string
  sourceResourceId?: string
  triggerEntityType?: string
  statuses?: WorkflowExecutionStatus[]
  pageSize?: number
  enabled?: boolean
  includeDisplayContext?: boolean
  refetchActiveMs?: number
}) {
  const selectedOrganizationId = useStore(
    organizationStore,
    (state) => state.selectedOrganizationId
  )

  const {
    workflowDefinitionId,
    sourceResourceId,
    triggerEntityType,
    statuses = [],
    pageSize = 25,
    enabled = true,
    includeDisplayContext = false,
    refetchActiveMs,
  } = params

  // Plain init shape (not create()): connect-query's useInfiniteQuery threads
  // the page param into the input per page. pageToken is the pageParamKey and
  // must be present as the first page's value ('').
  const input = useMemo(
    () => ({
      organizationId: selectedOrganizationId || '',
      workflowDefinitionId: workflowDefinitionId || undefined,
      sourceResourceId: sourceResourceId || undefined,
      triggerEntityType: triggerEntityType || undefined,
      statuses,
      pageSize,
      pageToken: '',
      includeDisplayContext,
    }),
    [
      selectedOrganizationId,
      workflowDefinitionId,
      sourceResourceId,
      triggerEntityType,
      statuses,
      pageSize,
      includeDisplayContext,
    ]
  )

  const query = useInfiniteQuery(listWorkflowExecutions, input, {
    pageParamKey: 'pageToken',
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
    enabled: !!selectedOrganizationId && enabled,
    staleTime: 30 * 1000,
    refetchInterval: refetchActiveMs
      ? (q) => {
          const executions =
            q.state.data?.pages.flatMap((page) => page.executions) ?? []
          const hasActive = executions.some(
            (execution) =>
              execution.status === WorkflowExecutionStatus.RUNNING ||
              execution.status === WorkflowExecutionStatus.PENDING
          )
          return hasActive ? refetchActiveMs : false
        }
      : false,
  })

  const executions = useMemo(
    () => query.data?.pages.flatMap((page) => page.executions) ?? [],
    [query.data]
  )

  return {
    executions,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  }
}

export function useGetWorkflowExecution(workflowExecutionId?: string, options?: { enabled?: boolean }) {
  const selectedOrganizationId = useStore(
    organizationStore,
    (state) => state.selectedOrganizationId
  )

  const request = useMemo(
    () =>
      create(GetWorkflowExecutionRequestSchema, {
        organizationId: selectedOrganizationId || '',
        workflowExecutionId: workflowExecutionId || '',
      }),
    [selectedOrganizationId, workflowExecutionId]
  )

  const query = useQuery(getWorkflowExecution, request, {
    enabled:
      !!selectedOrganizationId &&
      !!workflowExecutionId &&
      (options?.enabled ?? true),
    staleTime: 30 * 1000,
  })

  return {
    data: query.data,
    execution: query.data?.execution,
    steps: query.data?.steps || [],
    conditionTrace: query.data?.conditionTrace || [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

export function useRetryWorkflowExecution() {
  const selectedOrganizationId = useStore(
    organizationStore,
    (state) => state.selectedOrganizationId
  )
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(retryWorkflowExecution, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listWorkflowExecutions,
          transport,
          cardinality: undefined,
        }),
      })
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getWorkflowExecution,
          transport,
          cardinality: undefined,
        }),
      })
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listWorkflowDefinitions,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  const retryExecution = async (workflowExecutionId: string) => {
    if (!selectedOrganizationId) {
      throw new Error('No organization selected')
    }

    return mutation.mutateAsync(
      create(RetryWorkflowExecutionRequestSchema, {
        organizationId: selectedOrganizationId,
        workflowExecutionId,
      })
    )
  }

  return {
    mutation,
    retryExecution,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

export function useCancelWorkflowExecution() {
  const selectedOrganizationId = useStore(
    organizationStore,
    (state) => state.selectedOrganizationId
  )
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(cancelWorkflowExecution, {
    onSuccess: () => {
      // The cancel is async: the workflow flips status to Cancelled shortly
      // after on its disconnected context. Invalidate the execution list and
      // detail so the UI refetches and observes the terminal state.
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listWorkflowExecutions,
          transport,
          cardinality: undefined,
        }),
      })
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getWorkflowExecution,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  const cancelExecution = async (workflowExecutionId: string) => {
    if (!selectedOrganizationId) {
      throw new Error('No organization selected')
    }

    return mutation.mutateAsync(
      create(CancelWorkflowExecutionRequestSchema, {
        organizationId: selectedOrganizationId,
        workflowExecutionId,
      })
    )
  }

  return {
    mutation,
    cancelExecution,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

export function useStartWorkflowExecution() {
  const selectedOrganizationId = useStore(
    organizationStore,
    (state) => state.selectedOrganizationId
  )
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(startWorkflowExecution, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listWorkflowExecutions,
          transport,
          cardinality: undefined,
        }),
      })
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listWorkflowDefinitions,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  const startExecution = async (params: {
    workflowDefinitionId: string
    entityType: WorkflowTriggerEntityType
    sourceResourceId: string
  }) => {
    if (!selectedOrganizationId) {
      throw new Error('No organization selected')
    }

    return mutation.mutateAsync(
      create(StartWorkflowExecutionRequestSchema, {
        organizationId: selectedOrganizationId,
        workflowDefinitionId: params.workflowDefinitionId,
        entityType: params.entityType,
        sourceResourceId: params.sourceResourceId,
      })
    )
  }

  return {
    mutation,
    startExecution,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

export function useToggleWorkflow() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const listWorkflowDefinitionsKey = createConnectQueryKey({
    schema: listWorkflowDefinitions,
    transport,
    cardinality: undefined,
  })

  const mutation = useMutation(updateWorkflowDefinition, {
    onSuccess: (response) => {
      const updatedWorkflowDefinition = response.workflowDefinition

      if (updatedWorkflowDefinition) {
        const queryKey = createConnectQueryKey({
          schema: getWorkflowDefinition,
          transport,
          input: { id: updatedWorkflowDefinition.id },
          cardinality: 'finite',
        })

        queryClient.setQueryData(
          queryKey,
          create(GetWorkflowDefinitionResponseSchema, {
            workflowDefinition: updatedWorkflowDefinition,
          })
        )
      }

      queryClient.invalidateQueries({ queryKey: listWorkflowDefinitionsKey })
    },
  })

  const toggleWorkflow = async (id: string, enabled: boolean) => {
    return mutation.mutateAsync(
      create(UpdateWorkflowDefinitionRequestSchema, {
        id,
        enabled,
      })
    )
  }

  return {
    mutation,
    toggleWorkflow,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

export function useCreateWorkflowDefinition() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(createWorkflowDefinition, {
    onSuccess: (response) => {
      const newWorkflowDefinition = response.workflowDefinition

      if (!newWorkflowDefinition) return

      const queryKey = createConnectQueryKey({
        schema: getWorkflowDefinition,
        transport,
        input: { id: newWorkflowDefinition.id },
        cardinality: 'finite',
      })

      queryClient.setQueryData(
        queryKey,
        create(GetWorkflowDefinitionResponseSchema, {
          workflowDefinition: newWorkflowDefinition,
        })
      )

      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listWorkflowDefinitions,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

export function useUpdateWorkflowDefinition() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(updateWorkflowDefinition, {
    onSuccess: (response) => {
      const updatedWorkflowDefinition = response.workflowDefinition

      if (!updatedWorkflowDefinition) return

      const queryKey = createConnectQueryKey({
        schema: getWorkflowDefinition,
        transport,
        input: { id: updatedWorkflowDefinition.id },
        cardinality: 'finite',
      })

      queryClient.setQueryData(
        queryKey,
        create(GetWorkflowDefinitionResponseSchema, {
          workflowDefinition: updatedWorkflowDefinition,
        })
      )

      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listWorkflowDefinitions,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

export function useCloneWorkflowDefinition() {
  const selectedOrganizationId = useStore(
    organizationStore,
    (state) => state.selectedOrganizationId
  )
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(cloneWorkflowDefinitionRpc, {
    onSuccess: (response) => {
      const clonedWorkflowDefinition = response.workflowDefinition

      if (!clonedWorkflowDefinition) return

      const queryKey = createConnectQueryKey({
        schema: getWorkflowDefinition,
        transport,
        input: { id: clonedWorkflowDefinition.id },
        cardinality: 'finite',
      })

      queryClient.setQueryData(
        queryKey,
        create(GetWorkflowDefinitionResponseSchema, {
          workflowDefinition: clonedWorkflowDefinition,
        })
      )

      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listWorkflowDefinitions,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  const { mutateAsync } = mutation

  const cloneWorkflowDefinition = useCallback(
    async (sourceWorkflowDefinitionId: string) => {
      if (!selectedOrganizationId) {
        throw new Error('No organization selected')
      }

      return mutateAsync(
        create(CloneWorkflowDefinitionRequestSchema, {
          organizationId: selectedOrganizationId,
          sourceWorkflowDefinitionId,
        })
      )
    },
    [mutateAsync, selectedOrganizationId]
  )

  return {
    mutation,
    cloneWorkflowDefinition,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

export function useDeleteWorkflowDefinition() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(deleteWorkflowDefinitionRpc, {
    onSuccess: (_, variables) => {
      // Evict the deleted workflow's cache entry
      queryClient.removeQueries({
        queryKey: createConnectQueryKey({
          schema: getWorkflowDefinition,
          transport,
          input: { id: variables.id },
          cardinality: 'finite',
        }),
      })

      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listWorkflowDefinitions,
          transport,
          cardinality: undefined,
        }),
      })
    },
  })

  const { mutateAsync } = mutation

  const deleteWorkflow = useCallback(
    async (id: string) => {
      return mutateAsync(
        create(DeleteWorkflowDefinitionRequestSchema, {
          id,
        })
      )
    },
    [mutateAsync]
  )

  return {
    mutation,
    deleteWorkflow,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

export function useEvaluateWorkflowConditions(params: {
  conditions: WorkflowCondition | undefined
  entityType: WorkflowTriggerEntityType
  organizationId: string
}) {
  const { conditions, entityType, organizationId } = params

  const isValid =
    !!conditions &&
    entityType === WorkflowTriggerEntityType.INQUIRY &&
    !!organizationId

  const [debouncedConditions, setDebouncedConditions] = useState(conditions)
  const [debouncedEntityType, setDebouncedEntityType] = useState(entityType)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedConditions(conditions)
      setDebouncedEntityType(entityType)
    }, 500)
    return () => clearTimeout(timer)
  }, [conditions, entityType])

  const request = useMemo(
    () =>
      create(EvaluateWorkflowConditionsRequestSchema, {
        organizationId,
        entityType: debouncedEntityType,
        conditions: debouncedConditions,
      }),
    [organizationId, debouncedEntityType, debouncedConditions]
  )

  const query = useQuery(evaluateWorkflowConditions, request, {
    enabled: isValid,
    staleTime: 30 * 1000,
  })

  return {
    result: query.data,
    isLoading: query.isLoading && query.fetchStatus !== 'idle',
    isError: query.isError,
    error: query.error,
    hasLoaded: query.isSuccess || !!query.data,
  }
}
